import { Controller } from "@hotwired/stimulus"
import { MarkerClusterer } from "@googlemaps/markerclusterer"
import { useCookies } from "./mixins/useCookies"

const CLUSTER_COLOR = "#113C7B"

class BrandedClusterRenderer {
  render({count, position}, stats, map) {
    const svg = `<svg fill="${CLUSTER_COLOR}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240" width="50" height="50">
<circle cx="120" cy="120" opacity=".6" r="70" />
<circle cx="120" cy="120" opacity=".3" r="90" />
<circle cx="120" cy="120" opacity=".2" r="110" />
<text x="50%" y="50%" style="fill:#fff" text-anchor="middle" font-size="50" dominant-baseline="middle" font-family="roboto,arial,sans-serif">${count}</text>
</svg>`

    return new google.maps.Marker({
      position,
      icon: {
        url: `data:image/svg+xml;base64,${btoa(svg)}`,
        anchor: new google.maps.Point(25, 25),
      },
      title: `Cluster of ${count} locations`,
      zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
    })
  }
}

export default class extends Controller {
  static targets = [ "field", "map", "latitude", "longitude", "marker", "popup" ]
  static values = {
    imageurl: String,
    clickedimageurl: String,
    zoom: { type: Number, default: 10 },
    latitude: Number,
    longitude: Number,
    popupUrl: String,
  }

  connect() {
    useCookies(this)
    this.resetMarkers();
    this.cleanLocalStorage();

    const sidebar = document.getElementById("pagy");
    if (sidebar) {
      new MutationObserver(() => {
        const cardTitles = document.querySelectorAll('[id^="new_favorite"]');
        this.setTitleListeners(cardTitles);
      }).observe(sidebar, { subtree: true, childList: true });
    }

    this.handleResize = this.handleResize.bind(this)
    window.addEventListener("resize", this.handleResize)

    // More comprehensive check for Google Maps support
    if (typeof google !== "undefined" &&
        typeof google.maps !== "undefined" &&
        typeof google.maps.Map === "function" &&
        typeof google.maps.Marker === "function") {
      try {
        this.initMap()
      } catch (error) {
        console.error("Failed to initialize Google Maps:", error);
        this.displayBrowserNotSupportedMessage()
      }
    } else {
      console.warn("Google Maps API not properly loaded");
      this.displayBrowserNotSupportedMessage()
    }
  }

  disconnect() {
    window.removeEventListener("resize", this.handleResize)
  }

  // The desktop map block stays in the DOM (only CSS-hidden) on a mobile
  // list request, so Stimulus still connects this controller and the
  // google-maps-callback event still targets it. Without this check we'd
  // build every marker and the clusterer for a map the mobile user can't
  // see. Re-checked on resize so switching to a desktop viewport still
  // initializes the map on demand.
  isVisible() {
    return this.element.offsetParent !== null
  }

  handleResize() {
    if (this.map || !this.isVisible()) return

    this.initMap()
  }

  applyCityFromUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    const city = urlParams.get('city');

    const CITIES = {
      "Nashville" : { latitude: 36.16404968727089, longitude: -86.78125827725053 },
      "Atlantic City" : { latitude: 39.3625, longitude: -74.425 },
      "Search all": { latitude: 37.0902, longitude: -95.7129 },
      "Los Angeles": { latitude: 34.0522, longitude: -118.2437 },
    }

    if (city && CITIES[city]) {
      const { latitude, longitude } = CITIES[city];

      const alreadyReloaded = urlParams.get('reloaded');
      if (!alreadyReloaded) {
        urlParams.set('reloaded', 'true');
        window.location.search = urlParams.toString();
      }

      this.setCookie("latitude", latitude);
      this.setCookie("longitude", longitude);
      this.setCookie("city", city);

      this.map.setCenter({ lat: latitude, lng: longitude });
      this.map.setZoom(12);
    }
  }

  displayBrowserNotSupportedMessage() {
    const mapContainer = this.mapTarget;
    if (mapContainer) {
      mapContainer.innerHTML = `
        <div style='text-align: center; padding: 20px;'>
          <p>It looks like your browser is not supported for displaying the map.</p>
          <p>Please update your browser to the latest version or try using a different browser for the best experience.</p>
        </div>
      `;
    }
  }

  initialize() {
    this.markersArray = []
    this.mapMarkers = []
    this.image = this.imageurlValue
    this.clickedImage = this.clickedimageurlValue
    const cardTitles = document.querySelectorAll('[id^="new_favorite"]');
    this.setTitleListeners(cardTitles);
  }

  reloadPage() {
    window.location.search = window.location.search
  }

  // Left map popup functions start here
  // It was necessary to create this functions in this controller because the map is created here, and we have access to Markers.

  openMarker(event) {
    const event_id = event.currentTarget.id.replace(/\D/g, '');
    const marker = this.mapMarkers.find(marker => marker.id == event_id);

    marker.setIcon(this.clickedimageurlValue);
    google.maps.event.trigger(marker, "mouseover");

    sessionStorage.setItem('selected_marker', marker.id);
    this.resetMarkers(event_id);
  }

  hidePopup() {
    this.popupTarget.classList.add('hidden');
  }

  resetMarkers(event_id = null) {
    this.mapMarkers.forEach(marker => {
      if (marker.id !== event_id) {
        marker.setIcon(this.image);
        marker.setAnimation(null);
      }
    });
  }

  cleanLocalStorage() {
    sessionStorage.removeItem('selected_marker');
    sessionStorage.removeItem('marker_infowindow');
  }

  setTitleListeners(cardTitles) {
    cardTitles.forEach((title) => {
      title.addEventListener('click', this.openMarker.bind(this))
    })
  }

  // Left map popup functions end here

  scrollToSelectedLocation(){
    if(sessionStorage.getItem('marker_infowindow')) {
      let id = sessionStorage.getItem('marker_infowindow').split('_')[1]
      let card = document.getElementById(id)
      card.scrollIntoView({behavior: 'smooth', block: "nearest", inline: "nearest"})
    }
  }

  initMap() {
    if (!this.isVisible()) return

    this.map = new google.maps.Map(this.mapTarget, {
      center: new google.maps.LatLng(
        this.latitudeValue || this.getCookie("latitude") || Number(this.latitudeTarget.value) || 36.16404968727089,
        this.longitudeValue || this.getCookie("longitude") || Number(this.longitudeTarget.value) || -86.78125827725053
        ),
      zoom: this.zoomLevel(),
      mapTypeControl: true,
      mapTypeControlOptions: {
        style: google.maps.MapTypeControlStyle.DROPDOWN_MENU,
        position: google.maps.ControlPosition.TOP_CENTER
      },
      zoomControl: true,
      zoomControlOptions: {
        position: google.maps.ControlPosition.TOP_LEFT
      }
    })

    const image = this.imageurlValue
    const clickedImage = this.clickedimageurlValue

    if (this.hasFieldTarget) {
      this.autocomplete = new google.maps.places.Autocomplete(this.fieldTarget)
      this.autocomplete.bindTo('bounds', this.map)
      this.autocomplete.setFields(['address_components', 'geometry', 'icon', 'name'])
      this.autocomplete.addListener('place_changed', this.placeChanged.bind(this))

      this.marker = new google.maps.Marker({
        position: { lat: Number(this.latitudeTarget.value) , lng: Number(this.longitudeTarget.value) },
        map: this.map,
        icon: image
      });
      this.markersArray.push(this.marker)
    }

    if (this.hasMarkerTarget) {
      this.setMarkers(this.map, image, clickedImage);
    }else{
      this.marker = new google.maps.Marker({
        map: this.map,
        anchorPoint: new google.maps.Point(0, -29),
        icon: image
      })
    }

    const clickedLocation = this.hasPopupTarget;
    let selectedMarker = sessionStorage.getItem('selected_marker');
    if (clickedLocation) this.popupTarget.classList.remove('hidden');
    if (selectedMarker) {
      let marker = this.mapMarkers.find((marker) => { return marker.id == selectedMarker });
      marker.setIcon(clickedImage);
    }

    this.applyCityFromUrl()
  }

  setMarkers(map, image, clickedImage) {
    // Adds markers to the map, clustered instead of individually placed --
    // a broad search can return thousands of results, and putting that many
    // Marker + InfoWindow objects directly on the map froze the tab. Markers
    // are built without `map:` set so MarkerClusterer controls when each one
    // actually gets attached (grouped into a cluster icon, or shown
    // individually once zoomed in enough); every underlying location is
    // still represented, none are dropped, just visually grouped by density.
    let prevInfoWindow = false
    let pin = document.getElementById(sessionStorage.getItem('marker_infowindow'))

    this.markerTargets.forEach((markerTarget) => {
      const element = markerTarget;
      const latitudeTarget = Number(element.dataset.latitude)
      const longitudeTarget = Number(element.dataset.longitude)
      const element_id = element.id.replace(/\D/g, '');

      const marker = new google.maps.Marker({
        position: { lat: latitudeTarget, lng: longitudeTarget },
        icon: image,
        animation: google.maps.Animation.DROP,
        id: element_id
      });

      this.mapMarkers.push(marker)

      // Built on first interaction rather than upfront for every marker --
      // each one wraps a lazy-loading turbo-frame, and most markers in a
      // large result set are never clicked or hovered.
      let infowindow = null
      const getInfoWindow = () => {
        if (!infowindow) {
          infowindow = new google.maps.InfoWindow({ content: markerTarget, maxWidth: 210 })
        }
        return infowindow
      }

      marker.addListener("click", () => {
        this.mapMarkers.forEach((marker) => {
          marker.setIcon(image)
          marker.setAnimation(null);
        })

        // Opens popup(url is provided only on desktop)
        if (this.hasPopupUrlValue) {
          const turboFrame = document.getElementById('map-left-popup');
          turboFrame.setAttribute("src", this.popupUrlValue + element_id);
        }
        marker.setIcon(clickedImage);
        sessionStorage.setItem('selected_marker', marker.id);

        if (prevInfoWindow) {
          prevInfoWindow.close()
        }
        marker.setAnimation(null);

        prevInfoWindow = getInfoWindow()

        prevInfoWindow.open({
          anchor: marker,
          map,
          shouldFocus: false,
        });
        sessionStorage.setItem('marker_infowindow', element.id)
      })
      // Open infowindows eventlisteners registration
      marker.addListener("mouseover", this.debounce(function() {
        if (prevInfoWindow) {
          prevInfoWindow.close()
        }
        marker.setAnimation(null);

        prevInfoWindow = getInfoWindow()

        prevInfoWindow.open({
          anchor: marker,
          map,
          shouldFocus: false,
        });

        sessionStorage.setItem('marker_infowindow', element.id)
      }, 250));

      marker.addListener("mouseout", function() {
        clearTimeout(this.scheduledFuncId);
      });

      if (pin && pin.id == element.id) {
        prevInfoWindow = getInfoWindow()
        prevInfoWindow.open({
          anchor: marker,
          map,
          shouldFocus: false,
        });
        this.scrollToSelectedLocation()
      }
    });

    new MarkerClusterer({ map, markers: this.mapMarkers, renderer: new BrandedClusterRenderer() });
  }


  placeChanged() {
    this.clearMarkers()
    let place = this.autocomplete.getPlace()

    if (!place.geometry) {
      window.alert(`No details available for input: ${place.name}`)
      return
    }

    if (place.geometry.viewport) {
      this.map.fitBounds(place.geometry.viewport)
    } else {
      this.map.setCenter(place.geometry.location)
      this.map.setZoom(17)
    }

    this.marker.setPosition(place.geometry.location)
    this.marker.setVisible(true)

    this.latitudeTarget.value = place.geometry.location.lat()
    this.longitudeTarget.value = place.geometry.location.lng()
  }

  keydown(event) {
    if (event.key == "Enter") {
      event.preventDefault()
    }
  }

  clearMarkers() {
    for (var i = 0; i < this.markersArray.length; i++ ) {
      this.markersArray[i].setMap(null);
    }
    this.markersArray.length = 0;
  }

  debounce(func, delay) {
    this.scheduledFuncId;

    return function () {
      // Scheduled function executes just once
      clearTimeout(this.scheduledFuncId);
      this.scheduledFuncId = setTimeout(func, delay);
    };
  }

  zoomLevel() {
    if (this.getCookie("city") == "Search all") {
      return 3;
    } else {
      return this.zoomValue || 10;
    }
  }
}
